import { Fragment, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation, gql } from '@apollo/client'
import { useIonModal, useIonToast } from '@ionic/react'
import Card from '@mui/material/Card'
import UpgradeIcon from '@mui/icons-material/Upgrade'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import styled from 'styled-components'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { styled as mdStyled } from '@mui/material/styles'
import ErrorIcon from '@mui/icons-material/ErrorOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import ArrowRight from '@mui/icons-material/KeyboardArrowRight'
import { DateTime } from 'luxon'
import { SETTINGS_UPGRADE_PLAN_PATH } from '../path'
import { useLogFeatureInfo } from '../utils/session'
import Toast from '../utils/Toast'
import DateInfo from './DateInfo'
import './DataCard.css'
import { is_plan_on } from '../utils/global'
import { useToggleTowerForecast } from '../data/hooks/useToggleTowerForecast'
import QuotaExceededModal from './QuotaExceededModal'
import Ad from './Ad'

const ONE_MIN_IN_MS = 1000*60

// Must mirror STEP_24HOUR_FORECAST in the api-tower-reading resolver
// (app/src/towerReadings/select.js). EIGHT_HOURS was missing here, so that step silently fell
// back to 2 below and mislabelled the hour ranges.
const FORECAST_24HOURS_STEP_VALUES = {
	ONE_HOUR: 1,
	TWO_HOURS: 2,
	THREE_HOURS: 3,
	FOUR_HOURS: 4,
	SIX_HOURS: 6,
	EIGHT_HOURS: 8,
	TWELVE_HOURS: 12
}

const get_forecast_step_hours = () =>
	FORECAST_24HOURS_STEP_VALUES[process.env.REACT_APP_FORECAST_24HOURS_STEP as keyof typeof FORECAST_24HOURS_STEP_VALUES] || 2

const SET_DEFAULT_TOWER = gql`
mutation set_default_tower($id: ID!) {
	user_update(default_tower_id: $id) {
		data{
			id
			default_tower_id
		}
	}
}`

const MyCard = styled(Card)`
	align-self: center;
	position: relative;
	margin-top: -15px;
	height: var(--v-height); // 1070px; when all extra fields are in
	border-radius: 12px !important;

	& .upgrade {
		display: flex;
		justify-content: start;

		& .upgrade-btn {
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			height: 20px;
			background-color: black;
			color: white;
			border-radius: 30px;
			width: 112px;
			font-size: 10px;
			font-weight: 500;

			& .upgrade-text {
				display: flex;
				align-items: center;
				justify-content: center;
				margin-left: -5px;
			}

			& svg {
				font-size:16px;
			}
		}
	}

	& .forecast-container {
		position: relative;
		margin-bottom: 10px; 

		& .forecast-slider {
			position: relative;
			display: grid;
			width: 100%;
			grid-template-columns: 23px 1fr 23px;
			grid-template-rows: 73px;
			grid-template-areas: 'btnleft slider btnright';
		}

		& .forecast-btn {
			display: flex;
			align-items: center;
			justify-content: center;
			background-color: #1976d2;

			&:hover {
				background-color: #1c84ec;
			}

			&:active {
				background-color: #1976d2;
			}

			& svg {
				color: white;
			}
		}

		& .forecast-left-btn {
			grid-area: btnleft;
			border-radius: 7px 0px 0px 7px;
		}

		& .forecast-right-btn {
			grid-area: btnright;
			border-radius: 0px 7px 7px 0px;
		}

		& .forecast {
			grid-area: slider;
			display: flex;
			width: 100%;
			padding-top: 11px;
			overflow-x: scroll;
			position: absolute;
			border-top: 1px solid #00000012;
			border-bottom: 1px solid #00000012;
			box-sizing: border-box;

			& .forecast-item {
				display: flex;
				flex-direction: column;

				& .forecast-item-header {
					width: 60px;
					height: 20px;
					display: flex;
					justify-content: center;
					align-items: center;
					font-size: 14px;
					font-weight: 500;
				}

				& .forecast-item-body {
					width: 60px;
					height: 40px;
					display: flex;
					justify-content: center;
					align-items: center;

					& .unsafe-icon {
						color: #ef5350;
					}

					& .safe-icon {
						color: #1876D1;
					}

					& .unavailable {
						cursor: default;
					}

					& .upgrade-icon {
						cursor: pointer;
						color: white;
						border-radius: 30px;
						background-color: black;
						width: 20px;
						height: 20px;
					}
				}
			}
		}

		& .forecast::-webkit-scrollbar {
			display: none;
		}
	}

	@media (max-width: 450px) {
		transform: translate(-20px, 0px);
	}

	@media (max-width: 400px) {
		transform: translate(-35px, 0px);
	}
`

const RowItem = styled.div`
	display: grid;
	grid-template-columns: 7fr 4fr;
	align-items: center;
	width: 100%;
	min-width: 340px;
	height: 36px;

	& .label {
		display: flex;

		& .text {
			text-align: left;
			width: 183px;
			white-space: nowrap;
			overflow: hidden;
		}

		& .question {
			width: 20px;
			height: 20px;
			background-color: #e1e4e6;
			border-radius: 20px;
			display: flex;
			align-items: center;
			justify-content: center;
			position: absolute;
			top: -2px;
			left: 6px;
			cursor: pointer;

			& svg {
				font-size: 14px;
				opacity: 0.4;
			}

			&.hazard {
				left: -20px;
			}
		}
	}

	@media (max-width: 500px) {
		grid-template-columns: 10fr 4fr;
		min-width: 300px;
	}

	@media (max-width: 400px) {
		min-width: 270px;
	}
`

const Table = styled.div`
	margin: 10px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	@keyframes show_ad {
		from {
			top: 250px;
		}
		to {
			top: 0;
		}
	}

	& .hazard-data {
		border: 1px solid #0000004d;
		border-radius: 7px;
		padding: 4px 15px 6px 15px;
		margin-bottom: 12px;
		position: relative;
		overflow: hidden;

		& legend {
			margin-left: -2px;
			font-weight: 600;
			font-size: 14px;
			opacity: 0.7;
		}
	}
`

const HazardStatus = mdStyled(({ ...props }:any) => {
	return <Box {...props}>{props.children}</Box>
})(({ theme, danger, neutral }) => ({
	fontSize: '16px',
	color: neutral == 'true' ? 'black' : danger == 'true' ? theme.palette.error.light : theme.palette.primary.main,
	fontWeight: neutral == 'true' ? '400' : 'bold'
}))

const Header = styled.div`
	display: grid;
	padding: 15px 26px 20px 26px;
	grid-template-rows: 32px 24px 50px;
	grid-template-columns: 1fr 100px;
	grid-template-areas: 
		'title    icon'
		'time     time'
		'warning  warning';

	& .h-title {
		grid-area: title;
	}

	& .h-icon {
		grid-area: icon;

	}

	& .h-time {
		grid-area: time;
		display: flex;
		justify-content: flex-start;
		font-style: italic;
	}

	& .big-warning {
		grid-area: warning;
		margin-top: 5px;
		margin-bottom: -5px;
		border: 1px solid var(--ion-color-danger);
		border-radius: 6px;
		background-color: #eb445a12;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;

		& .h-warning {
			max-width: 300px;
			font-style: italic;
			font-size: 12px;
			color: var(--ion-color-danger);

			&.offline {
				font-size: 16px;
			}
		}
	}
`

const scrollForecast = (direction:string, node:any) => {
	const step = direction == 'right' ? 250 : -250
	return () => {
		if (node && node.current && node.current.scrollBy)
			node.current.scrollBy({ left: step, behavior: 'smooth' })
	}
}

// <div title="Present"><ErrorIcon className="unsafe-icon"/></div>
// <div title="Absent"><CheckCircleOutlineIcon className="safe-icon"/></div>
// #ef5350
const Row = (input:any) => {
	const { name, value, unit, hazard, onHelp, showHelp, hazardGroup } = input

	return (
	<RowItem>
		<div className="label">	
			<div className="text">{name}</div>	
			<div style={{ position:'relative' }}>	
				{showHelp && <div className={hazardGroup ? "question hazard" : "question"} onClick={onHelp}><QuestionMarkIcon/></div>}
			</div>
		</div>
		{hazard 
			? (<Box sx={{ display:'flex', alignItems:'center', gap:'6px' }}>
				<HazardStatus danger={value=='Present' ? "true" : "false"} neutral={value=='--' ? "true" : "false"}>
					{value}
				</HazardStatus>{value == 'Present' ? 
				<div title="Present" style={{ marginTop:'5px', color:'#ef5350' }}><ErrorIcon className="unsafe-icon"/></div> :
				<div title="Absent" style={{ marginTop:'5px', color:'#1976d2' }}><CheckCircleOutlineIcon className="safe-icon"/></div>}
			</Box>)
			: <div>{`${value||'--'}${unit ? ` ${unit}` : ''}`}</div>}
	</RowItem>)
}

const ForecastRow = (input:any) => {
	const node = useRef()
	const history = useHistory()
	const toast = new Toast(...useIonToast(), { duration: 5000 })

	const scrollLeft = scrollForecast('left', node)
	const scrollRight = scrollForecast('right', node)

	const { name, value, onHelp, showHelp, towerTz, upgrade, towerId, onRefresh } = input
	const forecastExists = value && value.length
	const now:any = new Date()
	let dt:any = DateTime.fromISO(new Date().toISOString())
	if (towerTz)
		dt = dt.setZone(towerTz)

	const nowHour = dt.get('hour')

	const addHour = (hour:number, hourAmount:number) => {
		const nextHour = hour + hourAmount
		const val = nextHour >= 24 ? nextHour - 24 : nextHour
		return !val ? '12AM' : val < 12 ? `${val}AM` : val == 12 ? '12PM' : `${val-12}PM`
	}

	const start_date = ((value||[])[0]||{}).start_date
	const end_date = ((value||[]).slice(-1)[0]||{}).end_date
	const relevantForecasts = forecastExists && start_date && end_date && start_date < end_date && new Date(start_date) <= now && new Date(end_date) > now

	// Local state for immediate loading feedback
	const [is_adding_forecast, set_is_adding_forecast] = useState(false)

	// Hook for toggle logic
	const {
		can_add_tower,
		toggle_tower,
		is_saving: toggle_loading,
		is_basic,
		is_pro,
		is_org
	} = useToggleTowerForecast({
		onCompleted: () => {
			set_is_adding_forecast(false)
			toast.show('24H forecast enabled successfully!')
			if (onRefresh)
				onRefresh()
		},
		on_error: (err) => {
			set_is_adding_forecast(false)
			console.error('Toggle error:', err)
			toast.show('Failed to enable 24H forecast. Please try again.', { error: true, closeText: 'close' })
		}
	})

	const is_paid_user = is_basic || is_pro || is_org

	// Modal for quota exceeded
	const [show_quota_modal, dismiss_quota_modal] = useIonModal(QuotaExceededModal, {
		current: can_add_tower().current,
		max: can_add_tower().max,
		plan_name: can_add_tower().plan_name || 'Free',
		onDismiss: () => {
			dismiss_quota_modal()
		},
		onGoToSearch: () => {
			dismiss_quota_modal()
			history.push('/search')
		},
		onGoToUpgrade: () => {
			dismiss_quota_modal()
			history.push(SETTINGS_UPGRADE_PLAN_PATH + '?plan=organization')
		}
	})

	const handle_add_forecast = () => {
		if (!towerId) {
			toast.show('Tower ID not available', { error: true, closeText: 'close' })
			return
		}

		// Check if user can add this tower
		const quota_check = can_add_tower(towerId)

		if (!quota_check.can) {
			// Show modal with quota info
			show_quota_modal()
			return
		}

		// Set loading immediately for instant feedback
		set_is_adding_forecast(true)

		// Proceed with toggle
		const result = toggle_tower(towerId, true)

		// If toggle failed due to team-toggled tower, show informative message
		if (!result.success && result.is_team_toggled) {
			set_is_adding_forecast(false)
			toast.show(result.error || 'This tower was enabled by your team and cannot be disabled.', { error: false, closeText: 'close' })
		}
	}

	const go_to_upgrade = () => {
		if (is_paid_user) {
			// For paid users, call the add forecast handler
			handle_add_forecast()
		} else {
			// For free users, redirect to upgrade page
			history.push(SETTINGS_UPGRADE_PLAN_PATH)
		}
	}

	const is_loading = is_adding_forecast || toggle_loading

	const data = !relevantForecasts ? [] : (value||[]).reduce((acc:any, x:any, i:number) => {
		const step = get_forecast_step_hours()
		const idx = step*i
		const startHour = addHour(nowHour, idx)
		const endHour = addHour(nowHour, idx+step)
		let unsafe = null
		if (x.start_date && x.end_date) {
			const start = new Date(x.start_date)
			const end = new Date(x.end_date)
			if (!acc.valid)
				acc.valid = start <= now && now < end
			unsafe = x.unsafe
		}
		if (acc.valid) {
			const item = {
				startHour,
				endHour,
				unsafe,
				el: (<div title="Unavailable" className="unavailable">--</div>)
			}
			if (unsafe === null)
				item.el = (<div title="Unavailable" className="unavailable">--</div>)
			else if (unsafe)
				item.el = (<div title="Present"><ErrorIcon className="unsafe-icon"/></div>)
			else
				item.el = (<div title="Absent"><CheckCircleOutlineIcon className="safe-icon"/></div>)

			if (upgrade && acc.data.length >= 2)
				item.el = (
					<div title={is_loading ? "Loading..." : "Upgrade"}>
						<UpgradeIcon
							className="upgrade-icon"
							onClick={is_loading ? undefined : go_to_upgrade}
							style={{
								cursor: is_loading ? 'wait' : 'pointer',
								opacity: is_loading ? 0.5 : 1
							}}
						/>
					</div>
				)
			acc.data.push(item)
		}
		return acc
	}, { valid:false, data:[] }).data

	return (<div className="forecast-container">
		<RowItem>
			<div className="label">
				<div className="text">{upgrade ? name.replace('24', '2') : name}</div>
				<div style={{ position:'relative' }}>
					{showHelp && <div className="question hazard" onClick={onHelp}><QuestionMarkIcon/></div>}
				</div>
			</div>{!upgrade ? null :
			<div className="upgrade">
				<div
					className={`upgrade-btn${is_paid_user ? ' paid-user' : ''}`}
					style={is_paid_user ? {
						backgroundColor: '#1976d2',
						color: 'white',
						cursor: (is_adding_forecast || toggle_loading) ? 'wait' : 'pointer',
						opacity: (is_adding_forecast || toggle_loading) ? 0.7 : 1
					} : {}}
					onClick={(is_adding_forecast || toggle_loading) ? undefined : go_to_upgrade}>
					<div className="upgrade-text">
						{(is_adding_forecast || toggle_loading) ? (
							<CircularProgress size={14} sx={{ color: 'white', marginRight: '4px' }} />
						) : (
							<UpgradeIcon/>
						)}
						{is_paid_user ? 'Add 24H forecast' : 'Get 24hrs forecast'}
					</div>
				</div>
			</div>}
		</RowItem>{!data.length ?
			<div>Not available</div> :
			<div className="forecast-slider">
				<button 
					onMouseDown={scrollLeft} 
					onTouchStart={scrollLeft} 
					className="forecast-btn forecast-left-btn"><ArrowLeft/></button>
				<div className="forecast" ref={node}>{data.map((d:any, i:any) => 
					<div className="forecast-item" key={i}>
						<div className="forecast-item-header">{d.endHour}</div>
						<div className="forecast-item-body">{d.el}</div>
					</div>)}
				</div>
				<button 
					onMouseDown={scrollRight} 
					onTouchStart={scrollRight} 
					className="forecast-btn forecast-right-btn"><ArrowRight/></button>
			</div>}
	</div>)
}

/**
 * Formula from https://github.com/GoannaAg/GoApp-Data/blob/868cab134122a7d3cdbddae7b423d88e484d2ad6/DataServices/GWeather.DataBase/Stored%20Procedures/Procs2/GWUpdateGWWeatherNowByLocationID.sql#L156
 * 
 * @param	{Number} humidity
 * @param	{Number} air_temp
 * 
 * @return	{Number} dew_point
 */
const compute_dew_point = (humidity:any, air_temp:any) => {
	humidity = humidity || 0
	air_temp = air_temp || 0
	const top =  243.12* ( Math.log( humidity/100) +  (17.62 * air_temp)/ (243.12+air_temp))
	const bottom  =  17.62 -  ( Math.log( humidity/100) +  (17.62 * air_temp)/ (243.12+air_temp))
	
	return bottom ? (top / bottom) : (top/0.1)
}

/**
 * Caclulates the "Grassland Fire Danger Index" (wind speed at 10 meter) or the 
 * "Grain Harvest Code of Practice" (wind speed at 2 meter).
 *
 * Definition: https://mesonet.com.au/about/observations
 * Formula: https://calculator.academy/grassland-fire-danger-index-calculator/
 * 
 * @param	{Number}	drought_factor				
 * @param	{Number}	temp				
 * @param	{Number}	humidity				
 * @param	{Number}	wind_speed		
 * 		
 * @return	{Number}	gfdi
 */
// const compute_gfdi = (drought_factor:number, temp:number, humidity:number, wind_speed:number) => {
// 	return 0.1 * drought_factor * (33 - (0.037 * temp)) * (1 - (0.01 * humidity)) * (wind_speed + 10)
// }

/**
 * Derives the NowCast label from the leading 24h-forecast buckets.
 *
 * The nowcast answers "is a hazardous inversion coming in the next 2 hours?". The leading forecast
 * buckets span that same window, so under V3 they replace the nowcast rather than sitting beside it
 * (see the 'unsafe' override below for why V3 and only V3).
 *
 * Returns '--' when the forecast has no opinion for that window. There is deliberately no fallback
 * to the nowcast or to V2: a card must be attributable to exactly one model.
 *
 * Caveat: with a step coarser than 2 hours a single bucket already exceeds the nowcast horizon, so
 * the label then describes that coarser window (over-conservative). Production runs ONE_HOUR.
 *
 * @param	{Array}		buckets		next_24_hours items, each { start_date, end_date, unsafe }
 *
 * @return	{String}	label		'Present', 'Absent' or '--'
 */
const derive_nowcast_from_forecast = (buckets:any[]) => {
	const buckets_in_2h = Math.max(1, Math.ceil(2/get_forecast_step_hours()))
	// Anchored to the reading, not to now - same anchor the nowcast itself uses. (ForecastRow drops
	// already-elapsed buckets for display; that filter is deliberately not applied here.)
	const horizon = (buckets||[]).slice(0, buckets_in_2h).filter((b:any) => b && b.unsafe !== null && b.unsafe !== undefined)

	if (!horizon.length)
		return '--'

	return horizon.some((b:any) => b.unsafe) ? 'Present' : 'Absent'
}

// Dedupe key so the trace below prints once per reading rather than on every React render
// (parseReading runs on each render, and Weather.tsx polls every 10 minutes).
let _v3_nowcast_log_key = ''

/**
 * Traces how the NowCast row was decided, so the V3 canary can be verified in the browser.
 * Only prints when V3 is active, and only once per distinct reading.
 *
 * @param	{Object}	input	{ reading_date, label, source, buckets, nowcast_scalar, nowcast_would_have_been }
 *
 * @return	{Void}
 */
const log_v3_nowcast_decision = ({ version, reading_date, label, source, buckets, nowcast_scalar, nowcast_would_have_been }:any) => {
	const key = `${version}|${reading_date}|${label}|${source}`
	if (key == _v3_nowcast_log_key)
		return
	_v3_nowcast_log_key = key

	const is_v3 = version == 'V3'
	const differs = is_v3 && nowcast_would_have_been != label
	console.groupCollapsed(`%c ${version} · NOWCAST = ${label} %c source: ${source}${differs ? '  ⚠ differs from nowcast' : ''}`,
		`color:#fff;background:${is_v3 ? '#c026d3' : '#0072B2'};padding:2px 6px;border-radius:3px;font-weight:bold`,
		'color:#666;font-weight:normal')
	console.log('forecast version      :', version, is_v3 ? '(default)' : '(pinned - not what production serves)')
	console.log('reading (UTC)         :', reading_date)
	console.log('NowCast row shows     :', label, `(from ${source})`)
	console.log('nowcast table would   :', nowcast_would_have_been, `(raw prob_exceedance = ${nowcast_scalar})`)
	console.log('forecast cells 0-1    :', is_v3 ? 'NOT overwritten (left as V3 forecast)' : 'overwritten with the nowcast verdict (V2 behaviour)')
	console.table((buckets||[]).map((b:any, i:number) => ({
		cell: i,
		start_utc: b?.start_date,
		end_utc: b?.end_date,
		unsafe: b?.unsafe
	})))
	console.groupEnd()
}

const parseReading = (reading:any, is_out_of_sync:boolean, is_offline:boolean, forecast_version:string, forecast_pinned:boolean) => {
	if (!reading)
		return []

	const {
		deltat_1,
		temp_1,
		tempdiff_h10_h1_avg,
		ws_2,
		ws_10,
		wd_10_label,
		wd_cv2_label,
		windgust_2,
		rain_acc,
		rain_prev_24h,
		solardown_10min,
		hazard_status,
		nowcast,
		next_24_hours,
		humidity
	} = reading

	const hazard_data_unavailable = hazard_status === null || hazard_status === undefined 
	const hazardStatus = hazard_data_unavailable ? '--' : hazard_status
	const hazardStatusCanon = `${hazardStatus}`.toLowerCase().trim()
	const next24Hours = (next_24_hours||[]).map(d => ({ ...d }))
	const is_v3 = forecast_version == 'V3'

	// A measured hazardous inversion outranks any prediction, under both versions.
	// V2: the NowCast row comes from the `nowcast` table, as it always has.
	// V3: it is derived from the leading forecast buckets instead, so the whole card speaks for one
	// model. V3 beats the nowcast on its own 2h horizon (88.3% vs 82.2% balanced accuracy, 94.1% vs
	// 84.3% of inversions caught) - see specs/-DONE/260708-02-v3-forecast-api-flag.
	const nowCast = hazardStatusCanon == 'present'
		? 'Present'
		: is_v3
			? derive_nowcast_from_forecast(next24Hours)
			: nowcast === null || nowcast === undefined || nowcast === ''
				? '--'
				: nowcast <= 0.5 ? 'Absent' : 'Present'

	// V2 only. The nowcast outperforms V2 at short range (82.2% vs 78.1%), so the first two forecast
	// cells are overwritten with it. That does not hold for V3, which beats the nowcast - overwriting
	// there would substitute the weaker model into the two cells where V3's margin is largest.
	if (!is_v3 && next24Hours && next24Hours.length >= 2 && (nowCast == 'Absent' || nowCast == 'Present')) {
		const end_date:any = new Date(next24Hours[1]?.end_date)
		if (end_date && !isNaN(end_date)) {
			const ellapsedHours = (end_date.getTime() - Date.now())/1000/60/60
			if (ellapsedHours > 0 && ellapsedHours < 2.3) {
				const mostReliableUnsafe = nowCast == 'Present'
				next24Hours[0].unsafe = mostReliableUnsafe
				next24Hours[1].unsafe = mostReliableUnsafe
			}
		}
	}
	if (hazard_data_unavailable)
		for (let i=0;i<next24Hours.length;i++)
			next24Hours[i].unsafe = null

	if (forecast_pinned)
		log_v3_nowcast_decision({
			version: forecast_version,
			reading_date: reading.date_utc,
			label: nowCast,
			source: hazardStatusCanon == 'present'
				? 'hazard_status (measured now, outranks both models)'
				: is_v3
					? `V3 forecast, leading ${Math.max(1, Math.ceil(2/get_forecast_step_hours()))} bucket(s)`
					: 'nowcast table (V2 behaviour)',
			buckets: next24Hours.slice(0, Math.max(1, Math.ceil(2/get_forecast_step_hours()))),
			nowcast_scalar: nowcast === null || nowcast === undefined || nowcast === '' ? 'null' : nowcast,
			nowcast_would_have_been: nowcast === null || nowcast === undefined || nowcast === ''
				? '--'
				: nowcast <= 0.5 ? 'Absent' : 'Present'
		})

	return [{
		key: 'hazard_status',
		label: 'Hazardous Inversion',
		description: `
		Labels meaning:
		</br>
		<strong style="color:#ef5350">Present</strong>: Hazardous Inversion is present
		</br>
		<strong style="color:#1976d2">Absent</strong>: No Hazardous Inversion
		</br>
		</br>
		When determining if weather conditions are suitable for spraying applicators <b>MUST</b> adhere to all label 
		requirements, operate using best practice and consider site specific conditions and the proximity 
		of sensitive vegetation (crops, native plants) and water ways. If a <b>Hazardous Inversion</b> is Present, 
		then no spraying is to be undertaken.
		</br>
		</br>
		If a Hazardous Inversion is <b>Absent</b>, however an <b>Inversion (Vert temp diff)</b> is present, represented by 
		a positive (+ ve) number, then conditions are suitable for spraying but extra caution may be needed. 
		Consider the direction of wind speed (2m) and max wind gust in relation to nearby sensitive areas 
		and avoid spraying under still conditions.
		</br>`,
		value: is_offline || is_out_of_sync ? '--' : hazardStatus,
		hazard: true
	}, {
		key: 'nowcast',
		label: 'NowCast',
		description: `
		Prediction of the Absence or Presence of a Hazardous Inversion occurring in the next 2 hours.
		</br>
		</br>
		Labels meaning:
		</br>
		<b>Present</b>: Hazardous inversion is present
		</br>
		<b>Absent</b>: No hazardous inversion`,
		value: is_offline || hazard_data_unavailable || is_out_of_sync ? '--' : nowCast,
		hazard: true
	}, {
		key: 'next_24_hours',
		label: '24 hours forecast',
		description: `
		<strong>Legend:</strong>
		</br>
		</br>
		<div style="color:#1976d2;display:flex;">
			<div title="Absent" style="margin-right:4px;width:20px;height:20px;">
				<svg style="fill:#1976d2;transform:scale(1.15);margin-top: 2px;" class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium safe-icon css-i4bv87-MuiSvgIcon-root" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="CheckCircleOutlineIcon"><path d="M16.59 7.58 10 14.17l-3.59-3.58L5 12l5 5 8-8zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"></path></svg>
			</div>
			<div style="margin-left:3px"><strong>Absent</strong></div>
		</div>
		When a tower has no inversion
		</br>
		</br>
		<div style="color:#ef5350;display:flex;">
			<div title="Present" style="margin-right:4px;width:20px;height:20px;">
				<svg style="fill:#ef5350;transform:scale(1.15);margin-top: 2px;" class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium unsafe-icon css-i4bv87-MuiSvgIcon-root" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="ErrorOutlineIcon"><path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"></path></svg>
			</div>
			<div style="margin-left:3px"><strong>Present</strong></div>
		</div>
		When a Hazardous Inversion is present. Do not spray.
		</br>
		</br>
		<div style="display:flex;">
			<div title="Upgrade" style="margin-right:4px;width:20px;height:20px;">
				<svg style="margin-top: 2px;background: black;border-radius: 30px;fill: white;" class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium upgrade-icon css-i4bv87-MuiSvgIcon-root" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="UpgradeIcon" style="background-color: black;border-radius: 30px;color: white;font-size: 21px;"><path d="M16 18v2H8v-2h8zM11 7.99V16h2V7.99h3L12 4 8 7.99h3z"></path></svg>
			</div>
			<div style="margin-left:3px"><strong>Upgrade</strong></div>
		</div>
		Upgrade to gain access to our 24 hours forecast, additional data and have up to 5 team members.
		</br>
		</br>
		Prediction of the Absence or Presence of a Hazardous Inversion occurring in the next 24 hours.
		`,
		value: is_offline ? '--' : next24Hours
	}, {
		key: 'tempdiff_h10_h1_avg',
		label: 'Inversion (Vert Temp Diff)',
		description: `An inversion occurs when temperature increases with height. It is measured as the Vertical Temperature 
		Difference (degrees C) between 1.25m and 10m above the surface. A positive (+ve) number represents the presence 
		of a surface temperature inversion.`,
		value: is_offline || tempdiff_h10_h1_avg === null || tempdiff_h10_h1_avg === undefined ? '--' : formatNbr(tempdiff_h10_h1_avg, 1, '°C')
	}, {
		key: 'ws_2',
		label: 'Wind Speed (2m)',
		description: `
		The 2m wind should to be recorded at the site of application as per Australian Pesticide and 
		Veterinary Medicines Authority (APVMA) advice. The wind reported is the average of the last 10 minutes. 
		The average wind speed at the application site during the period of application must not exceed the 
		maximum stated on the product label.
		</br>
		</br>
		The wind direction is true north and is the direction from which the wind is blowing.
		`,
		value: is_offline || ws_2 === null || ws_2 === undefined ? '--' : `${formatNbr(ws_2*3.6, 0, 'km/h')}${wd_cv2_label ? ` ${wd_cv2_label}` : ''}`
	}, {
		key: 'windgust_2',
		label: 'Max Wind Gust (2m)',
		description: `The maximum wind speed (gust) recorded during the last 10 minutes.`,
		value: is_offline || windgust_2 === null || windgust_2 === undefined ? '--' : formatNbr(windgust_2*3.6, 0, 'km/h')
	}, {
		key: 'ws_10',
		label: 'Wind Speed (10m)',
		description: `
		10 m winds are the winds observed and forecast by the Bureau of Meteorology (BoM) and news services. It is commonly referred to as the synoptic wind.
		The wind reported is the average of the last 10 minutes.
		</br>
		</br>
		The wind direction is true north and is the direction from which the wind is blowing.
		`,
		value: is_offline || ws_10 === null || ws_10 === undefined ? '--' : `${formatNbr(ws_10*3.6, 0, 'km/h')}${wd_10_label ? ` ${wd_10_label}` : ''}`
	}, {
		key: 'deltat_1',
		label: 'Delta T',
		description: `
		The difference between the wet and dry bulb temperature. It combines the evaporation effects of 
		temperature and relative humidity.
		</br>
		A common spray guideline is to spray when Delta T is between 2 and 8, with caution below 2 or above 10. 
		Between 10 and 12 conditions are marginal for COARSE or greater spray quality and unsuitable for Medium 
		or finer spray quality.
		`,
		value: is_offline || deltat_1 === null || deltat_1 === undefined ? '--' : formatNbr(deltat_1, 1, '°C')
	}, {
		key: 'temp_1',
		label: 'Temperature',
		description: `Standard air temperature is measured at 1.25m above the surface in a radiation screen.`,
		value: is_offline || temp_1 === null || temp_1 === undefined ? '--' : formatNbr(temp_1, 0, '°C')
	}, {
		key: 'solardown_10min',
		label: 'Solar Radiation',
		description: `
		Solar radiation (or light intensity) drives plant photosynthesis, and growth and reproductive 
		structure development. Solar radiation at Earth’s surface is typically defined as total radiation 
		across a wavelength rage of 280 to 4000 nm (shortwave radiation) and is expressed in Watts per square 
		meter which can be converted to MJ/m/unit of time.
		`,
		value: is_offline || solardown_10min === null || solardown_10min === undefined ? '--' : formatNbr(solardown_10min, 0, 'W/m2')
	}, {
		key: 'rain_acc',
		label: 'Rainfall from 9 am',
		paid: true,
		description: `The amount of rainfall received since 9:00 am.`,
		value: is_offline || rain_acc === null || rain_acc === undefined ? '--' : formatNbr(rain_acc, 0, 'mm')
	}, {
		key: 'rain_prev_24h',
		label: 'Rainfall 24 hrs to 9 am',
		paid: true,
		description: `The amount of rainfall received in the last 24 hour period until 9:00 am today.`,
		value: is_offline || rain_prev_24h === null || rain_prev_24h === undefined ? '--' : formatNbr(rain_prev_24h, 0, 'mm')
	}, {
		key: 'humidity',
		label: 'Relative Humidity',
		paid: true,
		description: `The average relative humidity for the past 10 minutes in percent.`,
		value: is_offline || humidity === null || humidity === undefined ? '--' : formatNbr(humidity, 0, '%')
	}, {
		key: 'dew_point',
		label: 'Dew Point',
		paid: true,
		description: `The dew point is the temperature at which air becomes saturated with moisture, causing water vapor to condense into dew. It's a key indicator of humidity levels.`,
		value: is_offline || humidity === null || humidity === undefined || temp_1 === null || temp_1 === undefined ? '--' : formatNbr(compute_dew_point(humidity,temp_1), 1, '°C')
	}, 
	// {
	// 	key: 'evapotranspiration',
	// 	label: 'Evapotranspiration',
	// 	description: `Evapotranspiration is the process by which water is transferred from the land to the atmosphere by evaporation from soil and other surfaces and by transpiration from plants.`,
	// 	value: '--'
	// }, {
	// 	key: 'thermal_time',
	// 	label: 'Thermal Time',
	// 	description: `Thermal Time is a measure of accumulated heat used to predict plant growth stages, calculated using temperature thresholds over a period.`,
	// 	value: '--'
	// }, {
	// 	key: 'vernalisation_time',
	// 	label: 'Vernalisation Time',
	// 	description: `Vernalization time refers to the required period of cold exposure for certain plants to initiate or accelerate flowering. It's essential for the development of some crops and flowering species.`,
	// 	value: '--'
	// }, {
	// 	key: 'adjusted_thermal_time',
	// 	label: 'Adjusted Thermal Time',
	// 	description: `Definition for this field coming soon...`,
	// 	value: '--'
	// }, {
	// 	key: 'grain_harvest_code_of_practice',
	// 	label: 'Grain Harvest Code of Practice',
	// 	description: `Definition for this field coming soon...`,
	// 	value: '--'
	// }, {
	// 	key: 'grass_fire_danger_index',
	// 	label: 'Grass Fire Danger Index',
	// 	description: `Definition for this field coming soon...`,
	// 	value: '--'
	// }
	]
}

const formatNbr = (nbr:any, decimals?:number, unit?:string) => {
	const n = nbr*1
	if (isNaN(n))
		return '--'

	return `${!decimals || decimals <= 0 ? Math.round(n) : n.toFixed(decimals)}${unit ? ` ${unit}` : ''}`
}

const convertDateToLocal = (date_utc?:string, tz?:string) => {
	let dt = DateTime.fromISO(date_utc||new Date().toISOString())
	if (tz)
		dt = dt.setZone(tz)

	return dt.toFormat('dd/MM/yyyy HH:mm')
}

const convertDateToNext2hoursLocalTime = (date_utc?:string, tz?:string) => {
	let dt = DateTime.fromISO(date_utc||new Date().toISOString()).plus({ hours: 2 })
	if (tz)
		dt = dt.setZone(tz)

	return dt.toFormat('HH:mm')
}

const CardFields = ({ ...props }) => {
	const { fields, mode, date_utc, towerTz, onHelp, access_24h, open_toggle_modal, towerId, onRefresh } = props
	const [hazard_status, nowcast, next_24_hours, ...rest] = fields||[]

	const plan_on = is_plan_on()

	const accessible_fields = !access_24h
		? rest.filter((x:any) => !x.paid)
		: rest

	return (!hazard_status || !nowcast || !next_24_hours ? null :
	<CardContent sx={{ paddingTop:'5px', backgroundColor:'white' }}>
		<Table>{access_24h ? null :
			<Ad/>}
			<fieldset className="hazard-data">
				<Row 
					id={hazard_status.key} 
					name={hazard_status.label} 
					value={hazard_status.value} 
					hazard={hazard_status.hazard}  
					showHelp={mode == 'now'}
					hazardGroup={true}
					onHelp={() => onHelp(hazard_status.label, hazard_status.description)} />{plan_on ? null : 
				<Row 
					id={nowcast.key} 
					name={`${nowcast.label} (until ${convertDateToNext2hoursLocalTime(date_utc, towerTz)})`} 
					value={nowcast.value} 
					hazard={nowcast.hazard}  
					showHelp={mode == 'now'}
					hazardGroup={true}
					onHelp={() => onHelp(nowcast.label, nowcast.description)} />}
				<ForecastRow
					id={next_24_hours.key}
					name={next_24_hours.label}
					value={next_24_hours.value}
					towerTz={towerTz}
					showHelp={mode == 'now'}
					upgrade={!access_24h}
					open_toggle_modal={open_toggle_modal}
					towerId={towerId}
					onRefresh={onRefresh}
					onHelp={() => onHelp(next_24_hours.label, next_24_hours.description)}/>
			</fieldset>{accessible_fields.map((p:any, key:number) => { return (
			<Row 
				id={p.key} 
				name={p.label == 'NowCast' ? `${p.label} (until ${convertDateToNext2hoursLocalTime(date_utc, towerTz)})` : p.label} 
				value={p.value} 
				hazard={p.hazard} 
				key={key} 
				showHelp={mode == 'now'}
				onHelp={() => onHelp(p.label, p.description)} />)})}
		</Table>
	</CardContent>)
}

const DataCard = ({ ...props }) => {
	const { 
		towerId, 
		towerName, 
		loading, 
		towerTz, 
		data, 
		defaultButton=true, 
		defaultTower=false, 
		onHelp, 
		mode, 
		onRefresh,
		dateSideGutter,
		classes,
		access_24h,
		open_toggle_modal,
		// 'V3' (default) or 'V2'. Owned by the page so the display rule always matches the version the
		// data in hand was fetched with, rather than a cookie re-read mid-render.
		forecast_version = 'V3',
		// True only when a secret param pinned this browser to a version. Gates the console trace so
		// ordinary production users - who are all on V3 now - get a silent console.
		forecast_pinned = false
	} = props

	// Mark as 'out of sync' when the latest data is greater than 2 hours ago
	const current_time = new Date(data?.date_utc).getTime()
	let is_out_of_sync = true
	let is_offline = false
	if (!isNaN(current_time)) {
		const ONE_HOUR_IN_MIN = 60
		const time_diff_in_min = Math.abs(Date.now() - current_time)/ONE_MIN_IN_MS
		is_out_of_sync = time_diff_in_min > 2*ONE_HOUR_IN_MIN
		is_offline = time_diff_in_min > 24*ONE_HOUR_IN_MIN
	}

	const obj:any = document.querySelector('.gradient-bg')
	if (obj?.style?.display !== undefined)
		obj.style.display = is_offline ? 'none' : ''

	const timeLabel = mode == 'now'
		? `As of ${convertDateToLocal(data?.date_utc, towerTz)}`
		: `As of ${convertDateToLocal(data?.date_utc, towerTz)} (${towerTz})`

	const [setDefaultTower] = useMutation(SET_DEFAULT_TOWER)
	const logFeat = useLogFeatureInfo()

	useEffect(() => {
		if (mode == 'now')
			logFeat({ title:'last_reading', content:towerName })
		else
			logFeat({ title:'spray_log', content:towerName })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [towerName, mode])

	const setDefault = (val:boolean) => {
		setDefaultTower({ variables: { id: val ? towerId : '' } })
		// alert(`WORK IN PROGRESS: Task for setting tower ID ${towerId} to default ${val} documented in GitHub issue https://github.com/GoannaAg/inversion-tower-doc/issues/3`)
	}

	return (
		<Fragment>
			{!data && loading && 
			<Box sx={{ marginTop:'50px' }} className="progressive-display">
				<CircularProgress/>
			</Box>}
			{(data || !loading) && 
			<div style={{ width:'100%', display:'flex', flexDirection:'column', marginTop:'10px', '--v-height':'810px' }} className={(classes||[]).join(' ')}>{mode == 'now' ?
				<DateInfo 
					tz={towerTz} 
					is_offline={is_offline}
					loading={loading} 
					sideGutter={dateSideGutter}
					lastReadingTime={data?.date_utc} 
					onRefresh={onRefresh}
					classes="datacard" /> : null}
				<MyCard className="progressive-display">
					<Header>
						<Typography variant="h6" sx={{ fontWeight:'bold' }} className="h-title">{towerName}</Typography>
						{defaultButton && <FormControlLabel
							value="start"
							control={<Switch 
								color="primary" 
								defaultChecked={defaultTower}
								onChange={(e:any) => setDefault(e.target.checked)} />}
							label={<Typography variant="body2" color="primary">Default</Typography>}
							labelPlacement="start"
							className="h-icon"
						/>}<Typography variant="body2" color="text.secondary" className="h-time">{is_offline ? '' : timeLabel}</Typography>
						<div className="big-warning">
							<Typography variant="body2" className={is_offline ? 'h-warning offline' : 'h-warning'}>
								{is_offline ? 'Tower offline' : 'All label requirements must be met including wind speed during periods of application.'}
							</Typography>
						</div>
					</Header>
					<Divider/>
					<CardFields
						fields={parseReading(data, is_out_of_sync, is_offline, forecast_version, forecast_pinned)}
						access_24h={access_24h}
						mode={mode}
						date_utc={data?.date_utc}
						towerTz={towerTz}
						onHelp={onHelp}
						open_toggle_modal={open_toggle_modal}
						towerId={towerId}
						onRefresh={onRefresh}/>
				</MyCard>
			</div>}
		</Fragment>
	)
}

export default DataCard
